import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ShopAuthLayout, { ArrowRightIcon } from './ShopAuthLayout';
import GoogleShopPicker from './GoogleShopPicker';
import VerificationField, { CODE_LENGTH } from './VerificationField';
import {
    checkShopSignupIdentifiers,
    getShopAuthState,
    sendShopSignupCodes,
    signupShopAccount,
    verifyShopSignupCode,
} from '../../utils/shopAuthApi';
import { getEmailValidationError } from '../../utils/emailValidation';
import { DEFAULT_COUNTRY, getPhoneValidationError } from '../../utils/phoneValidation';
import {
    SHOP_NAME_MAX,
    SPECIALTY_MAX,
    STREET_MAX,
    ZIP_LOOKUP_UNAVAILABLE_MESSAGE,
    ZIP_NOT_FOUND_MESSAGE,
    getShopDetailsErrors,
    matchZipCity,
    normalizeUsState,
    sanitizeZip5,
} from '../../utils/usShopAddress';
import useUsZipLookup from '../../hooks/useUsZipLookup';

const INITIAL_FORM = {
    ownerName: '',
    email: '',
    phone: '',
    password: '',
    shopName: '',
    streetAddress: '',
    city: '',
    region: '',
    postalCode: '',
    countryCode: 'US',
    specialty: '',
    isMobileMechanic: false,
};

// Typing stops at the limit; the validators enforce the same limits on save.
const SHOP_FIELD_MAX = { shopName: SHOP_NAME_MAX, streetAddress: STREET_MAX, specialty: SPECIALTY_MAX };

const FIELD_LABEL_CLASS = 'text-[11px] font-black uppercase tracking-[0.18em] text-[#606060]';
const EMPTY_IDENTIFIER_STATUS = { state: 'idle', message: '' };

const IDENTIFIER_COPY = {
    email: {
        checking: 'Checking email...',
        available: 'Email is available.',
        taken: 'A shop account already exists for this email. Try logging in or use a different email.',
        error: 'Could not check email right now.',
    },
    phone: {
        checking: 'Checking phone...',
        available: 'Phone is available.',
        taken: 'A shop account already exists for this phone number. Try logging in or use a different phone number.',
        error: 'Could not check phone right now.',
    },
};

// Reads ONE field's result from a /signup/check response or its 409 error. The server
// reports each field separately (`fields`); a field it didn't report is unknown (null),
// never assumed taken just because the other field collided.
export function identifierResult(key, payload) {
    const field = payload?.fields?.[key];
    if (!field || typeof field.available !== 'boolean') return null;
    return field.available
        ? { state: 'available', message: IDENTIFIER_COPY[key].available }
        : { state: 'taken', message: field.message || IDENTIFIER_COPY[key].taken };
}

function Field({ id, label, ...props }) {
    return (
        <label className="block" htmlFor={id}>
            <span className={FIELD_LABEL_CLASS}>{label}</span>
            <input
                id={id}
                className="mt-2 h-12 w-full rounded-xl border border-[#d8cbbd] bg-[#fbfaf8] px-4 text-[15px] font-semibold text-[#414141] shadow-[0_2px_7px_rgba(35,31,25,0.12)] outline-none transition placeholder:font-medium placeholder:text-[#a9a9a9] focus:border-[#D81F26] focus:ring-4 focus:ring-[#D81F26]/10 sm:text-base"
                {...props}
            />
        </label>
    );
}


// A Step 2 field's own validation message.
function FieldError({ id, message }) {
    if (!message) return null;
    return <p id={id} role="alert" className="mt-1.5 text-[11px] font-bold text-red-600">{message}</p>;
}


function IdentifierStatusMessage({ status, testId }) {
    if (!status?.message) return null;
    const className = status.state === 'taken'
        ? 'text-red-600'
        : status.state === 'available'
            ? 'text-[#1c5c3c]'
            : 'text-[#606060]';
    return <p data-testid={testId} className={`mt-1.5 text-[11px] font-bold ${className}`}>{status.message}</p>;
}


const PARTNER_PHONE_STYLES = `
.partner-phone-container { width: 100% !important; }

.partner-phone-container .form-control {
    width: 100% !important;
    height: 48px !important;
    padding-left: 64px !important;
    padding-right: 16px !important;
    font-size: 15px !important;
    font-weight: 600 !important;
    color: #414141 !important;
    background-color: #fbfaf8 !important;
    border: 1px solid #d8cbbd !important;
    border-radius: 12px !important;
    box-shadow: 0 2px 7px rgba(35, 31, 25, 0.12) !important;
    outline: none !important;
    font-family: inherit !important;
    transition: border-color .2s ease, box-shadow .2s ease !important;
}
.partner-phone-container .form-control:focus {
    border-color: #D81F26 !important;
    box-shadow: 0 0 0 4px rgba(90, 90, 90, 0.10) !important;
}
.partner-phone-container .form-control::placeholder { color: #a9a9a9 !important; font-weight: 500 !important; }
.partner-phone-container .form-control:disabled { opacity: .6 !important; }

.partner-phone-container .flag-dropdown {
    background-color: transparent !important;
    border: none !important;
    border-right: 1px solid #d8cbbd !important;
    border-radius: 12px 0 0 12px !important;
    width: 54px !important;
}
.partner-phone-container .flag-dropdown:hover,
.partner-phone-container .flag-dropdown.open { background-color: rgba(90, 90, 90, .06) !important; border-radius: 12px 0 0 12px !important; }
.partner-phone-container .selected-flag {
    width: 54px !important;
    padding-left: 12px !important;
    background: transparent !important;
    border-radius: 12px 0 0 12px !important;
    display: flex !important;
    align-items: center !important;
    gap: 6px !important;
}
.partner-phone-container .selected-flag .flag { position: static !important; margin: 0 !important; }
.partner-phone-container .selected-flag .arrow { position: static !important; margin: 0 !important; border-top-color: #606060 !important; }
.partner-phone-container .selected-flag .arrow.up { border-bottom-color: #606060 !important; }

.partner-phone-dropdown {
    margin-top: 6px !important;
    border: 1px solid #d8cbbd !important;
    border-radius: 12px !important;
    box-shadow: 0 18px 40px rgba(35, 31, 25, .18) !important;
    background: #ffffff !important;
    font-family: inherit !important;
    max-height: 260px !important;
}
.partner-phone-dropdown .search { padding: 10px !important; background: #ffffff !important; border-bottom: 1px solid #ece8e2 !important; }
.partner-phone-dropdown .search-emoji { display: none !important; }
.partner-phone-dropdown .search-box {
    width: 100% !important;
    margin: 0 !important;
    padding: 9px 12px !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    color: #414141 !important;
    background: #fbfaf8 !important;
    border: 1px solid #d8cbbd !important;
    border-radius: 9px !important;
    outline: none !important;
    font-family: inherit !important;
}
.partner-phone-dropdown .search-box:focus { border-color: #D81F26 !important; }
.partner-phone-dropdown .search-box::placeholder { color: #a9a9a9 !important; font-weight: 500 !important; }
.partner-phone-dropdown .country { padding: 9px 12px !important; display: flex !important; align-items: center !important; gap: 8px !important; }
.partner-phone-dropdown .country .country-name { font-size: 13px !important; font-weight: 600 !important; color: #414141 !important; }
.partner-phone-dropdown .country .dial-code { font-size: 12px !important; font-weight: 700 !important; color: #606060 !important; }
.partner-phone-dropdown .country:hover,
.partner-phone-dropdown .country.highlight { background-color: rgba(90, 90, 90, .08) !important; }
.partner-phone-dropdown::-webkit-scrollbar { width: 8px; }
.partner-phone-dropdown::-webkit-scrollbar-thumb { background: #d8cbbd; border-radius: 8px; }
`;

export default function ShopSignupPage() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [form, setForm] = useState(INITIAL_FORM);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY);
    const [googlePlaceId, setGooglePlaceId] = useState('');
    const [importedHours, setImportedHours] = useState([]);
    const [hoursNotice, setHoursNotice] = useState('');
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    // Step 2: one message per field, and the ZIP lookup that fills City and State.
    const [fieldErrors, setFieldErrors] = useState({});
    const zipLookup = useUsZipLookup(form.postalCode);
    const zipCities = zipLookup.status === 'found' ? zipLookup.info.cities : [];
    // Shown as soon as the lookup answers, without waiting for Continue.
    const zipStatusError = zipLookup.status === 'not_found'
        ? ZIP_NOT_FOUND_MESSAGE
        : zipLookup.status === 'error' ? ZIP_LOOKUP_UNAVAILABLE_MESSAGE : '';
    const [identifierStatus, setIdentifierStatus] = useState({
        email: EMPTY_IDENTIFIER_STATUS,
        phone: EMPTY_IDENTIFIER_STATUS,
    });

    // Step 3. One entry per channel so email and phone can be at different stages —
    // one verified while the other is still waiting on a resend is a normal state.
    const [codes, setCodes] = useState({ email: '', phone: '' });
    const [verified, setVerified] = useState({ email: false, phone: false });
    const [checking, setChecking] = useState({ email: false, phone: false });
    const [codeStatus, setCodeStatus] = useState({ email: '', phone: '' });
    const [sentTo, setSentTo] = useState({ email: '', phone: '' });
    const [cooldown, setCooldown] = useState({ email: 0, phone: 0 });
    const [isSendingCodes, setIsSendingCodes] = useState(false);
    // Codes are sent automatically when step 3 opens. StrictMode double-invokes effects
    // in development, and going back to edit a field re-enters the step, so without this
    // the owner would get two texts for one arrival.
    const autoSentRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        getShopAuthState()
            .then((data) => {
                if (!cancelled && data?.authenticated) navigate('/', { replace: true });
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const progress = useMemo(() => `${Math.round((step / 3) * 100)}%`, [step]);

    // One ticker for both channels — two intervals would drift apart on screen.
    useEffect(() => {
        if (cooldown.email <= 0 && cooldown.phone <= 0) return undefined;
        const timer = setInterval(() => {
            setCooldown((current) => ({
                email: Math.max(0, current.email - 1),
                phone: Math.max(0, current.phone - 1),
            }));
        }, 1000);
        return () => clearInterval(timer);
    }, [cooldown.email, cooldown.phone]);

    const clearFieldErrors = (...fields) => setFieldErrors((current) => {
        if (!fields.some((field) => current[field])) return current;
        const next = { ...current };
        fields.forEach((field) => { delete next[field]; });
        return next;
    });

    const updateField = (field) => (event) => {
        setForm((current) => ({ ...current, [field]: event.target.value }));
        if (field === 'email') {
            setIdentifierStatus((current) => ({ ...current, email: EMPTY_IDENTIFIER_STATUS }));
        }
        clearFieldErrors(field);
        if (error) setError('');
    };

    // ZIP: digits only, at most five, for typing and pasting alike (leading zeros kept).
    const handlePostalChange = (event) => {
        setForm((current) => ({ ...current, postalCode: sanitizeZip5(event.target.value) }));
        clearFieldErrors('postalCode', 'city', 'region');
        if (error) setError('');
    };

    // A verified ZIP sets the State and a City that belongs to it: the typed / Google city
    // if it is one of the ZIP's accepted names (in its official spelling), otherwise the
    // ZIP's main city. A ZIP that is incomplete or doesn't exist clears the State, so a
    // state from an earlier ZIP never lingers.
    useEffect(() => {
        if (zipLookup.status === 'found') {
            const { info } = zipLookup;
            setForm((current) => ({
                ...current,
                region: info.state,
                city: matchZipCity(current.city, info) || info.cities[0] || '',
            }));
            clearFieldErrors('postalCode', 'city', 'region');
        } else if (zipLookup.status === 'idle' || zipLookup.status === 'not_found') {
            setForm((current) => (current.region ? { ...current, region: '' } : current));
        }
    }, [zipLookup]);

    const e164Phone = useCallback(() => `+${String(form.phone).replace(/\D/g, '')}`, [form.phone]);

    // Debounced availability check for ONE field. Each field has its own status, its own
    // request and its own sequence number: a response is applied only if it is still the
    // latest request for that field, so a slow answer for an old value can never
    // overwrite the result for what is typed now, and never touches the other field.
    const identifierRequestRef = useRef({ email: 0, phone: 0 });
    const setFieldStatus = useCallback((key, status) => {
        setIdentifierStatus((current) => ({ ...current, [key]: status }));
    }, []);
    const scheduleIdentifierCheck = useCallback((key, value) => {
        const requestId = ++identifierRequestRef.current[key];
        const isCurrent = () => identifierRequestRef.current[key] === requestId;
        const controller = new AbortController();
        const timer = window.setTimeout(async () => {
            if (!isCurrent()) return;
            setFieldStatus(key, { state: 'checking', message: IDENTIFIER_COPY[key].checking });
            try {
                const data = await checkShopSignupIdentifiers({ [key]: value, signal: controller.signal });
                if (!isCurrent()) return;
                setFieldStatus(key, identifierResult(key, data) || { state: 'available', message: IDENTIFIER_COPY[key].available });
            } catch (err) {
                if (err?.name === 'AbortError' || !isCurrent()) return;
                // Only this field was sent, so a 409 without per-field detail is about it.
                const result = identifierResult(key, err?.data)
                    || (err?.status === 409 ? { state: 'taken', message: IDENTIFIER_COPY[key].taken } : null);
                setFieldStatus(key, result || { state: 'error', message: IDENTIFIER_COPY[key].error });
            }
        }, 650);
        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [setFieldStatus]);

    // Email and phone are normalized the way the server compares them (trimmed and
    // lower-cased email, E.164 digits for phone), so cosmetic edits don't re-check.
    const normalizedEmail = form.email.trim().toLowerCase();
    const normalizedPhone = getPhoneValidationError(form.phone, phoneCountry) ? '' : e164Phone();

    useEffect(() => {
        if (step !== 1) return undefined;
        if (!normalizedEmail || getEmailValidationError(normalizedEmail)) {
            identifierRequestRef.current.email += 1; // drop any in-flight answer
            setFieldStatus('email', EMPTY_IDENTIFIER_STATUS);
            return undefined;
        }
        return scheduleIdentifierCheck('email', normalizedEmail);
    }, [normalizedEmail, step, scheduleIdentifierCheck, setFieldStatus]);

    useEffect(() => {
        if (step !== 1) return undefined;
        if (!normalizedPhone) {
            identifierRequestRef.current.phone += 1;
            setFieldStatus('phone', EMPTY_IDENTIFIER_STATUS);
            return undefined;
        }
        return scheduleIdentifierCheck('phone', normalizedPhone);
    }, [normalizedPhone, step, scheduleIdentifierCheck, setFieldStatus]);

    // Applies one send response. Each channel reports its own outcome, so a text that
    // failed while the mail went through leaves the email side alone.
    const applySendResult = useCallback((data) => {
        const fallback = Number(data?.resend_after) > 0 ? Math.ceil(Number(data.resend_after)) : 60;
        ['email', 'phone'].forEach((key) => {
            const channel = data?.[key];
            if (!channel || channel.status === 'skipped') return;
            setSentTo((current) => ({ ...current, [key]: channel.sent_to || current[key] }));
            if (channel.status === 'sent') {
                setCooldown((current) => ({ ...current, [key]: fallback }));
                setCodeStatus((current) => ({ ...current, [key]: '' }));
            } else if (channel.status === 'throttled') {
                // The previous code is still live; show the real wait rather than
                // claiming a new one was sent.
                setCooldown((current) => ({
                    ...current,
                    [key]: Math.ceil(Number(channel.retry_after) || fallback),
                }));
            } else {
                setCodeStatus((current) => ({
                    ...current,
                    [key]: 'Could not send that code. Try again in a moment.',
                }));
            }
        });
    }, []);

    const sendCodes = useCallback(async (channel) => {
        setIsSendingCodes(true);
        try {
            const data = await sendShopSignupCodes({
                email: form.email.trim(),
                phone: e164Phone(),
                channel,
            });
            applySendResult(data);
            return true;
        } catch (err) {
            if (err?.status === 429 && err?.retryAfterSeconds) {
                const keys = channel ? [channel === 'sms' ? 'phone' : 'email'] : ['email', 'phone'];
                setCooldown((current) => {
                    const next = { ...current };
                    keys.forEach((key) => { next[key] = err.retryAfterSeconds; });
                    return next;
                });
            }
            setError(err?.message || 'Could not send the verification codes.');
            return false;
        } finally {
            setIsSendingCodes(false);
        }
    }, [applySendResult, e164Phone, form.email]);

    // Fired automatically on arrival at step 3 so there is no button to press before the
    // codes are even on their way.
    useEffect(() => {
        if (step !== 3 || autoSentRef.current) return;
        autoSentRef.current = true;
        sendCodes();
    }, [step, sendCodes]);

    const handleResend = (key) => async () => {
        setCodes((current) => ({ ...current, [key]: '' }));
        setCodeStatus((current) => ({ ...current, [key]: '' }));
        setError('');
        await sendCodes(key === 'phone' ? 'sms' : 'email');
    };

    // Checked the moment the sixth digit lands, so a wrong code surfaces here rather
    // than at submit. consume=False server-side, so the code survives for the submit.
    const handleCodeChange = (key) => async (value) => {
        setCodes((current) => ({ ...current, [key]: value }));
        setCodeStatus((current) => ({ ...current, [key]: '' }));
        if (error) setError('');
        if (value.length !== CODE_LENGTH) return;

        setChecking((current) => ({ ...current, [key]: true }));
        try {
            await verifyShopSignupCode({
                identifier: key === 'email' ? form.email.trim() : e164Phone(),
                channel: key === 'email' ? 'email' : 'sms',
                otp: value,
            });
            setVerified((current) => ({ ...current, [key]: true }));
        } catch (err) {
            setCodeStatus((current) => ({
                ...current,
                [key]: err?.message || 'That code is not right.',
            }));
        } finally {
            setChecking((current) => ({ ...current, [key]: false }));
        }
    };

    // Going back to change the email or phone makes both codes meaningless — they were
    // issued against the old values. Reset step 3 so returning re-sends rather than
    // showing a verified tick for an address that has since changed.
    const returnToDetails = () => {
        setError('');
        setStep(1);
        autoSentRef.current = false;
        setCodes({ email: '', phone: '' });
        setVerified({ email: false, phone: false });
        setCodeStatus({ email: '', phone: '' });
        setCooldown({ email: 0, phone: 0 });
    };

    const handlePhoneChange = (value, country) => {
        setForm((current) => ({ ...current, phone: String(value || '').replace(/\D/g, '') }));
        setIdentifierStatus((current) => ({ ...current, phone: EMPTY_IDENTIFIER_STATUS }));
        if (country?.dialCode) setPhoneCountry(country);
        if (error) setError('');
    };

    const validateStepOne = () => {
        if (!form.ownerName.trim()) return 'Your name is required.';
        if (!form.email.trim()) return 'Work email is required.';
        const phoneError = getPhoneValidationError(form.phone, phoneCountry);
        if (phoneError) return phoneError;
        if (form.password.length < 8) return 'Password must be at least 8 characters.';
        return '';
    };

    // Fills the shop fields from a chosen Google listing (already checked server-side to
    // be a US auto repair shop). Every field is replaced, so nothing from a previously
    // picked shop or earlier typing lingers; anything Google doesn't have is left blank
    // to fill in by hand. Everything stays editable.
    const handlePlaceSelected = (place) => {
        setForm((current) => ({
            ...current,
            shopName: place.name || '',
            streetAddress: place.street_address || '',
            city: place.city || '',
            region: normalizeUsState(place.region),
            postalCode: sanitizeZip5(place.postal_code),
            countryCode: 'US',
        }));
        setGooglePlaceId(place.google_place_id || '');
        setImportedHours(Array.isArray(place.hours) ? place.hours : []);
        setHoursNotice(place.hours_warning || '');
        setFieldErrors({});
        if (error) setError('');
    };

    // Every Step 2 field, checked the same way whether it came from Google or was typed:
    // US shops only, a ZIP that exists, and a city and state that belong to it. Drivers
    // search by ZIP and it is matched exactly, so it is required. Returns true if valid.
    const validateStepTwo = () => {
        const errors = getShopDetailsErrors(form, zipLookup);
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleContinue = async (event) => {
        event.preventDefault();
        const validationError = validateStepOne();
        if (validationError) {
            setError(validationError);
            return;
        }
        if (identifierStatus.email.state === 'taken' || identifierStatus.phone.state === 'taken') {
            // The field(s) at fault already show their own message.
            return;
        }
        // Checked before the shop details rather than at the end: being told "you already
        // have an account" after filling in the whole profile and waiting for two codes
        // is the worst possible moment to find out.
        setIsSubmitting(true);
        try {
            const data = await checkShopSignupIdentifiers({
                email: normalizedEmail,
                phone: e164Phone(),
            });
            ['email', 'phone'].forEach((key) => {
                const result = identifierResult(key, data);
                if (result) setFieldStatus(key, result);
            });
        } catch (err) {
            if (err?.status === 409) {
                // Mark exactly the field(s) the server says collided; the other keeps its
                // own result. Without per-field detail, fall back to the message wording.
                const results = ['email', 'phone'].map((key) => [key, identifierResult(key, err?.data)]);
                if (results.some(([, result]) => result)) {
                    results.forEach(([key, result]) => { if (result) setFieldStatus(key, result); });
                } else {
                    const message = err?.message || '';
                    if (/email/i.test(message)) setFieldStatus('email', { state: 'taken', message: IDENTIFIER_COPY.email.taken });
                    if (/phone/i.test(message)) setFieldStatus('phone', { state: 'taken', message: IDENTIFIER_COPY.phone.taken });
                }
                return;
            }
            setError(err?.message || 'Could not check those details.');
            return;
        } finally {
            setIsSubmitting(false);
        }
        setError('');
        setStep(2);
    };

    const handleContinueToVerify = (event) => {
        event.preventDefault();
        if (!validateStepTwo()) {
            setError('');
            return;
        }
        setError('');
        setStep(3);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!verified.email || !verified.phone) {
            setError('Enter both codes to finish.');
            return;
        }
        if (!validateStepTwo()) {
            setError('');
            setStep(2);
            return;
        }
        setIsSubmitting(true);
        setError('');
        try {
            await signupShopAccount({
                owner_name: form.ownerName,
                email: form.email,
                // E.164 so the country code is never ambiguous server-side;
                // auth/shop_models.normalize_phone accepts a leading "+".
                phone: `+${String(form.phone).replace(/\D/g, '')}`,
                password: form.password,
                shop_name: form.shopName.trim(),
                specialty: form.specialty.trim(),
                is_mobile_mechanic: form.isMobileMechanic,
                // Components rather than one line: country_code selects the holiday
                // calendar and postal_code is what location search will match on.
                street_address: form.streetAddress.trim(),
                city: form.city.trim(),
                region: normalizeUsState(form.region),
                postal_code: form.postalCode,
                country_code: 'US',
                // Only present when the owner used the Google lookup. The hours ride along
                // so the shop lands with its availability configured instead of blank.
                google_place_id: googlePlaceId || undefined,
                hours: importedHours.length ? importedHours : undefined,
                // Verified live in step 3; the server checks them again before it writes
                // anything, since a client-side tick proves nothing on its own.
                email_otp: codes.email,
                phone_otp: codes.phone,
            });
            navigate('/', { replace: true });
        } catch (err) {
            // A code can lapse between verifying it and pressing submit. Clear the ticks
            // so the owner is asked for a fresh code rather than staring at a green check
            // beside a box the server just rejected.
            if (err?.status === 401) {
                setVerified({ email: false, phone: false });
                setCodes({ email: '', phone: '' });
            }
            setError(err?.message || 'Could not create shop account.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Blocks Continue while either field is taken; each field shows its own message.
    const hasIdentifierConflict = identifierStatus.email.state === 'taken' || identifierStatus.phone.state === 'taken';

    return (
        <ShopAuthLayout>
            <style>{PARTNER_PHONE_STYLES}</style>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#D81F26] sm:text-[11px]">Shop portal setup</p>
                    <h1 className="mt-1.5 text-2xl font-black leading-tight tracking-tight text-[#414141] sm:mt-2 sm:text-[28px]">
                        {step === 1 && "Let's get acquainted."}
                        {step === 2 && 'Tell us about the shop.'}
                        {step === 3 && 'Two codes are on the way.'}
                    </h1>
                    <p className="mt-2 text-[13px] font-semibold leading-relaxed text-[#606060] sm:mt-2.5 sm:text-sm">
                        {step === 1 && 'Your account owner details first.'}
                        {step === 2 && 'A few details help your team feel at home.'}
                        {step === 3 && 'Enter both to confirm we can reach you. They expire in a few minutes.'}
                    </p>
                </div>
                <p className="mt-1 shrink-0 font-mono text-[11px] font-bold text-[#6B6B6B]">0{step} / 03</p>
            </div>

            <div className="mt-6 h-1 rounded-full bg-[#ece8e2]">
                <div className="h-full rounded-full bg-[#D81F26] transition-all duration-300" style={{ width: progress }} />
            </div>

            <form
                className="mt-7 space-y-5 sm:mt-8"
                onSubmit={step === 1 ? handleContinue : step === 2 ? handleContinueToVerify : handleSubmit}
            >
                {step === 1 ? (
                    <>
                        <Field id="shop-owner-name" label="Your name" autoComplete="name" placeholder="Jordan Lee" value={form.ownerName} onChange={updateField('ownerName')} required />
                        <div>
                            <Field id="shop-owner-email" label="Work email" type="email" autoComplete="email" placeholder="jordan@shop.com" value={form.email} onChange={updateField('email')} required />
                            <IdentifierStatusMessage status={identifierStatus.email} testId="email-status" />
                        </div>
                        <div className="block">
                            <span className={FIELD_LABEL_CLASS}>Phone number</span>
                            <div className="mt-2">
                                <PhoneInput
                                    country={DEFAULT_COUNTRY.countryCode}
                                    value={form.phone}
                                    onChange={handlePhoneChange}
                                    placeholder="303 555 0148"
                                    enableSearch={true}
                                    searchPlaceholder="Search country or code…"
                                    disableSearchIcon={true}
                                    disableCountryGuess={true}
                                    countryCodeEditable={false}
                                    containerClass="partner-phone-container"
                                    dropdownClass="partner-phone-dropdown"
                                    inputProps={{ id: 'shop-owner-phone', autoComplete: 'tel' }}
                                />
                            </div>
                            <IdentifierStatusMessage status={identifierStatus.phone} testId="phone-status" />
                        </div>
                        <Field id="shop-owner-password" label="Create a password" type="password" autoComplete="new-password" placeholder="8+ characters" value={form.password} onChange={updateField('password')} required />
                    </>
                ) : step === 3 ? (
                    <>
                        <VerificationField
                            id="shop-verify-email"
                            label="Work email"
                            sentTo={sentTo.email || form.email}
                            value={codes.email}
                            onChange={handleCodeChange('email')}
                            verified={verified.email}
                            checking={checking.email}
                            cooldown={cooldown.email}
                            onResend={handleResend('email')}
                            disabled={isSubmitting || isSendingCodes}
                            status={codeStatus.email}
                        />
                        <VerificationField
                            id="shop-verify-phone"
                            label="Phone number"
                            sentTo={sentTo.phone || `+${String(form.phone).replace(/\D/g, '')}`}
                            value={codes.phone}
                            onChange={handleCodeChange('phone')}
                            verified={verified.phone}
                            checking={checking.phone}
                            cooldown={cooldown.phone}
                            onResend={handleResend('phone')}
                            disabled={isSubmitting || isSendingCodes}
                            status={codeStatus.phone}
                        />
                        <p className="text-center text-[12px] font-semibold text-[#606060]">
                            Wrong email or number?{' '}
                            <button
                                type="button"
                                onClick={returnToDetails}
                                disabled={isSubmitting}
                                className="font-black text-[#D81F26] underline-offset-2 transition hover:text-[#414141] hover:underline disabled:opacity-60"
                            >
                                Change them
                            </button>
                        </p>
                    </>
                ) : (
                    <>
                        {/* One row, not a panel: the search itself lives in a modal so
                            this step does not get any longer than it already is. */}
                        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-dashed border-[#d5d5d5] bg-[#F9F9F9] px-4 py-3">
                            <span className="text-[12px] font-semibold text-[#606060]">
                                {googlePlaceId ? 'Details filled in from Google.' : 'Save time — pull your details from Google.'}
                            </span>
                            <button
                                type="button"
                                onClick={() => setIsPickerOpen(true)}
                                disabled={isSubmitting}
                                className="shrink-0 rounded-lg border border-[#D9D9D9] bg-white px-3 py-1.5 text-[12px] font-black text-[#414141] transition hover:border-[#414141] hover:bg-[#F2F2F2] disabled:opacity-60"
                            >
                                {googlePlaceId ? 'Change shop' : 'Find my shop'}
                            </button>
                        </div>
                        <div>
                            <Field id="shop-name" label="Shop name" autoComplete="organization" placeholder="Kella Auto Service" value={form.shopName} onChange={updateField('shopName')} maxLength={SHOP_FIELD_MAX.shopName} aria-invalid={Boolean(fieldErrors.shopName)} aria-describedby="shop-name-error" required />
                            <FieldError id="shop-name-error" message={fieldErrors.shopName} />
                        </div>
                        <div>
                            <Field id="shop-street" label="Street address" autoComplete="street-address" placeholder="123 Main St" value={form.streetAddress} onChange={updateField('streetAddress')} maxLength={SHOP_FIELD_MAX.streetAddress} aria-invalid={Boolean(fieldErrors.streetAddress)} aria-describedby="shop-street-error" required />
                            <FieldError id="shop-street-error" message={fieldErrors.streetAddress} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                {/* A ZIP with several accepted city names offers them as suggestions. */}
                                <Field id="shop-city" label="City" autoComplete="address-level2" placeholder="Denver" value={form.city} onChange={updateField('city')} list={zipCities.length > 1 ? 'shop-city-options' : undefined} aria-invalid={Boolean(fieldErrors.city)} aria-describedby="shop-city-error" required />
                                {zipCities.length > 1 && (
                                    <datalist id="shop-city-options">
                                        {zipCities.map((name) => <option key={name} value={name} />)}
                                    </datalist>
                                )}
                                <FieldError id="shop-city-error" message={fieldErrors.city} />
                            </div>
                            <div>
                                {/* Set from the ZIP code, so it can't disagree with it. */}
                                <Field id="shop-region" label="State / region" autoComplete="address-level1" placeholder="CO" value={form.region} onChange={updateField('region')} readOnly={zipLookup.status === 'found'} aria-invalid={Boolean(fieldErrors.region)} aria-describedby="shop-region-error" />
                                <FieldError id="shop-region-error" message={fieldErrors.region} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Field id="shop-postal" label="Postal code" autoComplete="postal-code" inputMode="numeric" pattern="[0-9]*" maxLength={5} placeholder="80202" value={form.postalCode} onChange={handlePostalChange} aria-invalid={Boolean(fieldErrors.postalCode || zipStatusError)} aria-describedby="shop-postal-error" required />
                                <FieldError id="shop-postal-error" message={fieldErrors.postalCode || zipStatusError} />
                            </div>
                            <div>
                                <Field
                                    id="shop-country"
                                    label="Country code"
                                    placeholder="US"
                                    maxLength={2}
                                    value={form.countryCode}
                                    aria-invalid={Boolean(fieldErrors.countryCode)}
                                    aria-describedby="shop-country-error"
                                    onChange={(event) => {
                                        setForm((current) => ({
                                            ...current,
                                            countryCode: event.target.value.toUpperCase().slice(0, 2),
                                        }));
                                        clearFieldErrors('countryCode');
                                    }}
                                />
                                <FieldError id="shop-country-error" message={fieldErrors.countryCode} />
                            </div>
                        </div>
                        <div>
                            <Field id="shop-specialty" label="Primary specialty" placeholder="General repair & diagnostics" value={form.specialty} onChange={updateField('specialty')} maxLength={SHOP_FIELD_MAX.specialty} aria-invalid={Boolean(fieldErrors.specialty)} aria-describedby="shop-specialty-error" required />
                            <FieldError id="shop-specialty-error" message={fieldErrors.specialty} />
                        </div>
                        <label htmlFor="shop-mobile-mechanic" className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#d8cbbd] bg-[#fbfaf8] px-4 py-3 shadow-[0_2px_7px_rgba(35,31,25,0.08)]">
                            <input
                                id="shop-mobile-mechanic"
                                type="checkbox"
                                checked={form.isMobileMechanic}
                                onChange={(event) => setForm((current) => ({ ...current, isMobileMechanic: event.target.checked }))}
                                className="mt-0.5 h-4 w-4 accent-[#D81F26]"
                            />
                            <span>
                                <span className="block text-sm font-black text-[#414141]">We are a mobile mechanic</span>
                                <span className="mt-0.5 block text-xs font-semibold leading-relaxed text-[#606060]">Drivers will see that your team can come to their vehicle.</span>
                            </span>
                        </label>
                        {importedHours.length > 0 && (
                            <p className="rounded-xl border border-[#cfe3d6] bg-[#f1faf4] px-4 py-2.5 text-[12px] font-semibold text-[#1c5c3c]">
                                Weekly hours imported from Google. You can adjust them in Settings once you're in.
                                {hoursNotice ? ` ${hoursNotice}` : ''}
                            </p>
                        )}
                    </>
                )}

                {error ? (
                    <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] font-bold text-red-700">{error}</p>
                ) : null}

                <div className={step === 1 ? '' : 'grid grid-cols-[76px_1fr] gap-3'}>
                    {step > 1 ? (
                        <button
                            type="button"
                            className="h-12 rounded-xl border border-[#e2d7ca] bg-[#fbfaf8] text-[12px] font-black uppercase tracking-[0.12em] text-[#414141] transition hover:bg-white active:scale-[0.99] sm:text-[11px]"
                            onClick={() => {
                                // Step 3 → 2 is just the shop details; the codes were
                                // issued against the step 1 contact fields and stay valid.
                                // Step 2 → 1 reopens those fields, so it has to go through
                                // returnToDetails and drop any codes already verified —
                                // otherwise changing the email keeps a green tick that
                                // belongs to the old address.
                                if (step === 3) {
                                    setError('');
                                    setStep(2);
                                } else {
                                    returnToDetails();
                                }
                            }}
                            disabled={isSubmitting}
                        >
                            Back
                        </button>
                    ) : null}
                    <button
                        type="submit"
                        disabled={isSubmitting || (step === 1 && hasIdentifierConflict) || (step === 3 && (!verified.email || !verified.phone))}
                        className="flex h-12 w-full items-center justify-center gap-2.5 rounded-xl bg-[#D81F26] px-4 text-[12px] font-black uppercase tracking-[0.16em] text-white transition hover:bg-[#B8181E] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 sm:text-[11px]"
                    >
                        {step === 1 && (isSubmitting ? 'Checking...' : 'Continue')}
                        {step === 2 && 'Continue'}
                        {step === 3 && (isSubmitting ? 'Opening portal...' : 'Open shop portal')}
                        <ArrowRightIcon className="h-4 w-4 shrink-0" />
                    </button>
                </div>
            </form>

            <p className="mt-7 text-center text-[12px] font-semibold text-[#606060]">
                Already have access? <Link className="font-black text-[#D81F26] transition hover:text-[#414141]" to="/login">Sign in</Link>
            </p>

            <GoogleShopPicker
                open={isPickerOpen}
                onClose={() => setIsPickerOpen(false)}
                onSelect={handlePlaceSelected}
                initialQuery={form.shopName}
            />
        </ShopAuthLayout>
    );
}
